import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import * as React from 'react';

import type { VariantProps } from 'class-variance-authority';

import { cn } from './lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-xs border font-medium w-fit whitespace-nowrap shrink-0 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        success:
          'border-transparent bg-status-success text-white [a&]:hover:bg-status-success/90',
        warning:
          'border-transparent bg-status-warning text-white [a&]:hover:bg-status-warning/90',
        outline:
          'text-muted-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground rounded-sm',
      },
      size: {
        sm: 'px-2 h-4 text-[0.625rem]',
        default: 'px-3 h-5.5 text-[0.625rem]',
        lg: 'px-2.5 h-6.5 text-xs',
        xl: 'px-3.5 h-8 text-sm',
      },
      tone: {
        none: '',
        success:
          'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
        info: 'border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400',
        warning:
          'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
        error:
          'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400',
      },
      shape: {
        default: '',
        pill: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      tone: 'none',
      shape: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  size,
  tone,
  shape,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size, tone, shape }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
